/**
 * Création de run (SPEC-UI §3.8, partie « nouvelle run »).
 *
 * On demande le strict nécessaire — un nom de chaîne (3-24 caractères, arbitrage A41 : c'est
 * une métadonnée de run, pas un champ du `GameState`), qui l'on joue ⟦A-4 §2⟧, le mode, et
 * depuis ⟦A-141 · C17⟧ **où la partie vit**.
 *
 * **Deux différences selon l'endroit où la partie sera calculée**, et elles sont dites plutôt
 * que cachées :
 *
 * - la **graine** ne se choisit qu'en bac à sable. `POST /api/runs` tire la sienne et ne la
 *   publie jamais (§5.4) : c'est elle qui rend les événements et les patterns d'Atelier
 *   imprévisibles. Un champ « graine » côté compte serait un champ qui ne fait rien ;
 * - la **tolérance d'accessibilité** part avec la création côté compte (§5.2), parce qu'elle
 *   vit dans l'état (`flags.accessibility_tolerance`) et pilote la génération des patterns.
 *
 * ⟦A-145 · C20⟧ **La création devient une conversation.** Décision de Mathieu du 11/09/2026,
 * après quatre maquettes (A-D) puis la synthèse E, verbatim en tête de
 * `docs/chantiers/C20-la-conversation.md` : *« j'aime la fonctionnalité du B (la boîte de
 * dialogue, ça fait très Nintendo), l'efficacité du C (un système de fenêtres réutilisable
 * partout), la beauté du D (ultra épuré, sans fond de fenêtre). […] il faudrait rajouter un
 * avatar de moi qui suit la personne : c'est moi le robot, c'est moi qui explique le jeu et qui
 * pose les questions au joueur, une conversation entre l'avatar que le joueur a choisi et
 * moi. »* Les trois cartes crème (`.carte-etape`/`.carte-genre`, ⟦A-142 · C18⟧) disparaissent :
 * chaque étape pose désormais une `<BoiteDialogue>` (Matt, l'hôte, qui parle : §1 bis, consigne
 * du 12/09, « appelle-moi Matt, M-A-T-T », jamais « Mathieu » à l'écran) en bas d'écran, et des
 * `<ChoixFlottants>` (les réponses du joueur) posés à même le décor, sans panneau. Tout l'ÉTAT
 * (nom, genre, mode, monde, graine) et toute la LOGIQUE (validation, création de run,
 * confirmation hardcore) restent inchangés : seul le rendu change, comme l'avait déjà fait
 * ⟦A-142⟧ pour l'ancien formulaire à un seul écran.
 *
 * ⟦C57 · lot 6 · A-174⟧ **Moins de texte.** La création était déjà sobre (24 à 52 mots par étape) : trois
 * retouches, et rien ne disparaît. (1) La fiche « ce que ça change » n'est plus un lien « en détail » qui monte
 * sa propre fenêtre (focus piégé à la main, Échap, Tab) : c'est le bouton partagé « 📰 Les dessous »
 * (`components/LesDessous.tsx`), qui porte la fiche INTACTE (`CeQuiChange.tsx` n'a pas bougé d'une virgule :
 * sujet sensible, textes relus et validés ⟦A-27⟧). (2) L'aide du champ nom tient en une phrase courte.
 * (3) La confirmation hardcore tient en une phrase ; les deux modes et les deux mondes, en toutes lettres,
 * sont dans les dessous de l'étape ③. Les trois répliques de Matt (⟦A-145 · C20⟧, définitives) n'ont pas bougé.
 *
 * ⟦C62 · lot tableau · A-179⟧ **Simple et limpide.** Réglage de contenu `creation_limpide` (défaut éteint : tout ce
 * qui précède, au caractère près). Allumé, l'étape ③ ne pose plus Mode, « Où vit ta partie » ni Graine : des défauts
 * silencieux (Normal, la partie locale ou le monde déjà choisi, la graine au hasard, voir `reglages.ts` pour le
 * pourquoi) et un lien « Options » discret qui rouvre ces choix tels quels. La réplique de Matt de l'étape ③ ne
 * parle plus d'un réglage ; le bouton « Lancer la chaîne » prend le focus (Entrée valide).
 */

import { useEffect, useRef, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";

import type { RunMode } from "@maxime/shared";

import { BoiteDialogue, type Interlocuteur } from "../../components/BoiteDialogue";
import { ChampBloc } from "../../components/ChampBloc";
import { ChoixFlottants } from "../../components/ChoixFlottants";
import { DessousBloc, LesDessous } from "../../components/LesDessous";
import { LogoMeTube } from "../../components/LogoMeTube";
import { definirMode, modeActif, type ModeJeu } from "../../game/client";
import type { GenreAvatar } from "../../game/gameClient";
import { spriteHeros } from "../../game/heros";
import { useIntroStore } from "../../stores/introStore";
import { useSessionStore } from "../../stores/sessionStore";
import { dialog, useUIStore } from "../../stores/uiStore";
import { useRunStore } from "../../stores/runStore";

import { CeQuiChange } from "./CeQuiChange";
import { LABEL_PRET_A_FILMER, REPLIQUE_PRET_A_FILMER, creationLimpide, reglagesVisibles } from "./reglages";
import { FondMenu } from "../Menu/FondMenu";

const NOM_MIN = 3;
const NOM_MAX = 24;
const NOM_DEFAUT = "Castelclic";

type Etape = 1 | 2 | 3;

const LABEL_ETAPE: Record<Etape, string> = {
  1: "Le nom de ta chaîne",
  2: "Qui tu joues",
  3: "Les réglages",
};

/** ⟦A-145 · C20, §1 bis⟧ Les répliques de l'hôte, DÉFINITIVES (chantier §2), non modifiables
 *  sans avenant. Espaces insécables avant `! : ?`, comme la maquette validée. Consigne de
 *  Mathieu du 12/09, verbatim : « appelle-moi Matt, M-A-T-T » : l'hôte s'appelle « Matt » à
 *  l'écran, jamais « Mathieu » (bandeau, répliques, libellé sous le portrait, placeholder). */
const REPLIQUE_ETAPE: Record<Etape, string> = {
  1: "Salut ! Moi c'est Matt. Je vais t'accompagner de zéro à un million. D'abord : comment s'appelle ta chaîne ?",
  2: "Et derrière la caméra, c'est qui ?",
  3: "Dernier réglage avant de filmer.",
};

/** L'hôte, Matt (⟦A-145 · C20, §1 bis⟧). `portrait-mathieu.png` (nom de FICHIER technique,
 *  inchangé, livré par un autre chantier, commit `7d015cf`) ; `<BoiteDialogue>` essaie le
 *  fichier et se replie sur le placeholder « M »/« Matt » au premier `onError` s'il venait à
 *  manquer, exactement le mécanisme d'`IconeAsset`. */
const HOTE: Interlocuteur = { nom: "Matt", portrait: "/avatar/portrait-mathieu.png" };

/** Le joueur : n'existe dans la boîte qu'à partir du choix du genre (étape ②), et le reste à
 *  l'étape ③ (chantier §2, colonne « Réponses flottantes »). */
function joueur(genre: GenreAvatar): Interlocuteur {
  return { nom: "Toi", portrait: `/avatar/portrait-${genre}.png` };
}

/** En-tête commune aux trois étapes : logo + titre à gauche, fil ① ② ③ en cases à droite
 *  (maquette E). Remplace l'ancien `<FilEtapes>` (glyphes ①②③ en texte, flottant seul sur le
 *  voile) : VT323 ne couvre pas ces glyphes, les cases sont des formes CSS avec de vrais
 *  chiffres. */
function EnteteConversation({ courante, limpide }: { courante: Etape; limpide: boolean }) {
  // ⟦C62 · lot tableau⟧ Création limpide : l'étape ③ ne pose plus de réglage, son libellé non plus.
  const label = courante === 3 && limpide ? LABEL_PRET_A_FILMER : LABEL_ETAPE[courante];
  return (
    <header className="conv-entete">
      <div className="conv-entete-lockup">
        <LogoMeTube className="conv-entete-logo" />
        <span className="conv-entete-titre">MeTube Studio</span>
      </div>
      <nav className="conv-fil" aria-label={`Étape ${courante} sur 3 — ${label}`}>
        {([1, 2, 3] as const).map((n) => (
          <span
            key={n}
            aria-hidden="true"
            className={`conv-fil-case ${n === courante ? "actif" : n < courante ? "fait" : ""}`}
          >
            {n}
          </span>
        ))}
      </nav>
    </header>
  );
}

export function NouvelleRun() {
  const creer = useRunStore((s) => s.creer);
  const choisirMonde = useSessionStore((s) => s.choisir);
  const tolerance = useUIStore((s) => s.settings.tolerance);
  const naviguer = useNavigate();
  const compte = modeActif() === "compte";
  // ⟦C62 · lot tableau · A-179⟧ La création limpide (`content/meta.json → creation_limpide`) : l'étape ③ ne pose
  // plus Mode, « Où vit ta partie » ni Graine d'office. Les valeurs ci-dessous SONT les défauts silencieux
  // (Normal, le monde déjà en cours, la graine au hasard : voir `reglages.ts`) ; « Options » rouvre les choix.
  const limpide = creationLimpide();
  const [options, setOptions] = useState(false);
  const reglages = reglagesVisibles(limpide, options);
  const formulaireEtape3 = useRef<HTMLFormElement>(null);
  const [etape, setEtape] = useState<Etape>(1);
  // ⟦C17⟧ Une vraie valeur, modifiable — pas un placeholder qu'on peut croire déjà rempli
  // (le défaut vécu du chantier). `onFocus` sélectionne tout : la remplacer d'un coup de
  // clavier est aussi rapide que de partir d'un champ vide.
  const [nom, setNom] = useState(NOM_DEFAUT);
  const [mode, setMode] = useState<RunMode>("normal");
  // ⟦C17⟧ Présélectionné (le premier de la liste) : aucun choix ne doit bloquer en silence.
  // ⟦C57 · lot 6⟧ La fiche « ce que ça change » vit dans les dessous (bouton partagé) : plus d'état
  // d'ouverture ni de focus piégé à la main ici, `LesDessous` s'en charge (Échap, Tab, retour du focus).
  const [genre, setGenre] = useState<GenreAvatar>("f");
  const [graine, setGraine] = useState("");
  const [erreur, setErreur] = useState<string | null>(null);
  const [champErrone, setChampErrone] = useState<"nom" | null>(null);
  const [enCours, setEnCours] = useState(false);

  // ⟦C62 · lot tableau⟧ Sans carte de réglage à l'étape ③, plus rien n'y prend le focus au montage (c'était
  // `autoFocus` sur la carte Normal, ⟦C18 bis · constat 1⟧) : après un clic souris sur « Continuer → », Entrée ne
  // validerait plus rien. Le bouton « Lancer la chaîne » le reçoit à sa place.
  useEffect(() => {
    if (etape !== 3 || !limpide || options) return;
    formulaireEtape3.current?.querySelector<HTMLButtonElement>('button[type="submit"]')?.focus();
  }, [etape, limpide, options]);

  /** Change d'étape ET efface toute erreur de l'étape qu'on quitte — jamais un message qui
   *  traîne d'un écran à l'autre. */
  function irA(cible: Etape) {
    setErreur(null);
    setChampErrone(null);
    setEtape(cible);
  }

  /** Retour depuis l'étape 1 : hors de la création, à l'écran-titre — `voirMenu()` ne touche ni
   *  au mode mémorisé ni à une éventuelle run (il n'y en a pas encore ici), exactement le même
   *  aller non destructif que « Menu principal » (`Reglages.tsx`). */
  function retourMenu() {
    useSessionStore.getState().voirMenu();
  }

  function validerNom(e: FormEvent) {
    e.preventDefault();
    const propre = nom.trim();
    if (propre.length < NOM_MIN || propre.length > NOM_MAX) {
      setErreur(`Le nom de chaîne fait entre ${NOM_MIN} et ${NOM_MAX} caractères.`);
      setChampErrone("nom");
      return;
    }
    irA(2);
  }

  function validerGenre(e: FormEvent) {
    e.preventDefault();
    irA(3);
  }

  async function soumettre(e: FormEvent) {
    e.preventDefault();
    if (mode === "hardcore") {
      const ok = await dialog.confirm({
        title: "Hardcore : une seule vie",
        // ⟦C57 · lot 6⟧ Une phrase (avant : « En hardcore, un game over est définitif : la chaîne finit au
        // mémorial, et les tuiles négatives frappent 20 % plus fort. Tu es sûr ? »). Le « +20 % » est sur la
        // carte du mode ; la phrase entière est dans les dessous de l'étape ③.
        body: "Un game over est définitif : la chaîne finit au mémorial.",
        confirmLabel: "Je suis sûr",
        cancelLabel: "Finalement non",
        danger: true,
      });
      if (!ok) return;
    }
    // ⟦C17⟧ **Le bug qu'a coûté la reprise automatique en le testant** : quand le monde reste
    // sur son défaut (bac à sable, ⟦A-141⟧ ne force plus un clic explicite via `ChoixDeMode`),
    // aucun `onChange` du radio ne se déclenche — donc `sessionStore.choisir()` non plus, donc
    // `maxime.mode.v1` ne s'écrivait JAMAIS. La partie se créait très bien (le client actif par
    // défaut EST le bac à sable), mais au rechargement suivant `modeMemorise()` rendait `null`
    // et `initialiser()` atterrissait sur le menu plutôt que de reprendre la partie. On pose
    // donc le mode explicitement ICI, au moment où le choix devient réel — qu'il ait été cliqué
    // ou laissé sur son défaut.
    definirMode(compte ? "compte" : "bac-a-sable");
    setEnCours(true);
    // ⟦C22 · A-151⟧ L'intro monte AVANT que `creer()` ne résolve : « l'intro joue par-dessus
    // pendant que la run se crée » (le chantier), pas seulement une fois la réponse revenue.
    // Elle est montée par `App.tsx`, pas ici (`stores/introStore.ts` explique pourquoi : `run`
    // apparaît dans `runStore` dès que `creer()` résout, et `router.tsx` bascule DE CET ÉCRAN
    // vers la chambre à cette même milliseconde, un `<Intro>` niché ici serait démonté avant sa
    // première image, constaté en jouant).
    useIntroStore.getState().demarrer(nom.trim(), genre);
    try {
      await creer({
        channelName: nom.trim(),
        genre,
        mode,
        seed: graine.trim() || undefined,
        toleranceAssistee: tolerance,
      });
      useIntroStore.getState().marquerPret();
    } catch (e) {
      // Côté compte, la création traverse le réseau : elle peut échouer (serveur injoignable,
      // session expirée). Le bac à sable, lui, n'échoue jamais ici — mais le `catch` ne coûte
      // rien et évite un écran figé sur « Lancer la chaîne » désactivé. `run` n'a jamais été posé
      // dans `runStore` : `Routeur` n'a jamais quitté cet écran, `annuler()` retire juste
      // l'intro, et le message d'erreur s'affiche ici normalement.
      useIntroStore.getState().annuler();
      setErreur(e instanceof Error ? e.message : "La création a échoué. Réessaie.");
    } finally {
      setEnCours(false);
    }
  }

  return (
    <main className="ecran-titre conv-ecran">
      <FondMenu />
      <div className="conv-voile" aria-hidden="true" />
      <div className="conv-cadre">
        <EnteteConversation courante={etape} limpide={limpide} />

        {etape === 1 ? (
          <>
            <form className="zone-flottante" onSubmit={validerNom}>
              <ChampBloc
                label="Nom de la chaîne"
                value={nom}
                onChange={(v) => {
                  setNom(v);
                  setErreur(null);
                  setChampErrone(null);
                }}
                // ⟦C57 · lot 6⟧ Une phrase (avant : « … caractères. C'est ce nom qui ira sur la plaque. »).
                aide={`${NOM_MIN} à ${NOM_MAX} caractères, il ira sur la plaque.`}
                maxLength={NOM_MAX}
                autoFocus
                selectionnerAuFocus
                invalide={champErrone === "nom"}
              />

              {erreur ? <p className="erreur">{erreur}</p> : null}

              <div className="menu-boutons">
                <ChoixFlottants variante="plat" label="Continuer →" type="submit" />
                <ChoixFlottants
                  variante="plat"
                  style="discret"
                  label="← Retour au menu"
                  onClick={retourMenu}
                />
              </div>

              <button type="button" className="lien" onClick={() => naviguer("/historique")}>
                📜 Historique des parties
              </button>
            </form>
            <BoiteDialogue hote={HOTE} texte={REPLIQUE_ETAPE[1]} />
          </>
        ) : null}

        {etape === 2 ? (
          <>
            <form className="zone-flottante" onSubmit={validerGenre}>
              <ChoixFlottants
                variante="portrait"
                nom="genre"
                onChange={(id) => setGenre(id as GenreAvatar)}
                options={[
                  {
                    id: "f",
                    label: "Créatrice",
                    portrait: "/avatar/portrait-f.png",
                    portraitRepli: (
                      <img
                        src={spriteHeros("idle", "f", "vert")}
                        alt=""
                        className="pixel-asset bouton-portrait-img"
                      />
                    ),
                    actif: genre === "f",
                    // ⟦C18 bis · constat 1⟧ Le premier contrôle de l'étape reçoit le focus AU
                    // MONTAGE : l'étape est un bloc conditionnel (démonté/remonté à chaque
                    // passage), donc `autoFocus` s'y applique à chaque fois. Sans ça, après un
                    // clic souris sur « Continuer → », Entrée ne valide plus rien tant qu'on n'a
                    // pas re-cliqué une carte.
                    autoFocus: genre === "f",
                  },
                  {
                    id: "h",
                    label: "Créateur",
                    portrait: "/avatar/portrait-h.png",
                    portraitRepli: (
                      <img
                        src={spriteHeros("idle", "h", "vert")}
                        alt=""
                        className="pixel-asset bouton-portrait-img"
                      />
                    ),
                    actif: genre === "h",
                    autoFocus: genre === "h",
                  },
                ]}
              />

              {/* ⟦C57 · lot 6⟧ « Ce que ça change » : la fiche entière, INTACTE, derrière le bouton partagé
                  « 📰 Les dessous » (avant : le lien « ▸ ce que ça change, en détail » qui ouvrait sa propre
                  fenêtre ; ⟦C21 bis · point 0⟧ avait déjà sorti la fiche du flux du formulaire, un dépli en
                  ligne l'envoyait hors écran : `LesDessous` se rend en portail, même garantie). */}
              <div className="coulisses">
                <LesDessous titre="Ce que ça change">
                  <CeQuiChange genre={genre} />
                </LesDessous>
              </div>

              <div className="menu-boutons">
                <ChoixFlottants variante="plat" label="Continuer →" type="submit" />
                <ChoixFlottants variante="plat" style="discret" label="← Retour" onClick={() => irA(1)} />
              </div>
            </form>
            <BoiteDialogue hote={HOTE} joueur={joueur(genre)} texte={REPLIQUE_ETAPE[2]} />
          </>
        ) : null}

        {etape === 3 ? (
          <>
            <form className="zone-flottante" ref={formulaireEtape3} onSubmit={(e) => void soumettre(e)}>
              {/* ⟦C62 · lot tableau · A-179⟧ Création limpide : Mode, « Où vit ta partie » et Graine ne sont plus posés
                  d'office, ils vivent derrière le lien « Options » plus bas. Création d'avant : toujours là. */}
              {reglages ? (
                <>
                  <ChoixFlottants
                    variante="reglage"
                    nom="mode"
                    accroche="Mode"
                    onChange={(id) => setMode(id as RunMode)}
                    options={[
                      {
                        id: "normal",
                        titre: "Normal",
                        sousTitre: "reprise après un échec",
                        title: "Un game over te laisse reprendre. On apprend en tombant.",
                        actif: mode === "normal",
                        // ⟦C18 bis · constat 1⟧ Même mécanisme qu'à l'étape 2 : la carte déjà
                        // choisie reprend le focus à chaque montage du bloc.
                        autoFocus: mode === "normal",
                      },
                      {
                        id: "hardcore",
                        titre: "Hardcore",
                        sousTitre: "une seule vie, +20 % de risque",
                        title: "Une seule vie, tuiles négatives +20 %. La chaîne finit au mémorial.",
                        actif: mode === "hardcore",
                        autoFocus: mode === "hardcore",
                      },
                    ]}
                  />

                  {/* ⟦A-141 · C17⟧ Le choix qui vivait avant dans un écran à part (`ChoixDeMode`,
                      disparu) : une ligne de ce parcours, en langage joueur, le jargon
                      (« simulation en base », « localStorage ») passe en infobulle. */}
                  <ChoixFlottants
                    variante="reglage"
                    nom="monde"
                    accroche="Où vit ta partie"
                    onChange={(id) => void choisirMonde(id as ModeJeu)}
                    options={[
                      {
                        id: "bac-a-sable",
                        titre: "Locale",
                        sousTitre: "tout de suite, sans compte",
                        title:
                          "Le moteur tourne dans ton navigateur : aucun compte, aucun serveur. Vider le cache efface la partie, rien n'y est vérifié.",
                        actif: !compte,
                        disabled: enCours,
                      },
                      {
                        id: "compte",
                        titre: "Compte",
                        sousTitre: "suit tous tes appareils",
                        title:
                          "La simulation tourne sur le serveur, en base, à chaque action : le hardcore engage vraiment, une victoire se grave.",
                        actif: compte,
                        disabled: enCours,
                      },
                    ]}
                  />

                  {compte ? null : (
                    <ChampBloc
                      label="Graine (optionnel)"
                      value={graine}
                      onChange={setGraine}
                      placeholder="laisse vide pour le hasard"
                    />
                  )}

                  {/* ⟦C57 · lot 6⟧ Les deux modes et les deux mondes, en toutes lettres : ce que les cartes disaient
                      en infobulle (`title`, inchangées) et la phrase entière de la confirmation hardcore. */}
                  <div className="coulisses">
                    <LesDessous titre="Modes et mondes">
                      <DessousBloc titre="Normal">
                        <p className="dessous-texte">Un game over te laisse reprendre. On apprend en tombant.</p>
                      </DessousBloc>
                      <DessousBloc titre="Hardcore">
                        <p className="dessous-texte">
                          Une seule vie. En hardcore, un game over est définitif : la chaîne finit au mémorial,
                          et les tuiles négatives frappent 20 % plus fort.
                        </p>
                      </DessousBloc>
                      <DessousBloc titre="Locale">
                        <p className="dessous-texte">
                          Le moteur tourne dans ton navigateur : aucun compte, aucun serveur. Vider le cache efface
                          la partie, rien n'y est vérifié.
                        </p>
                      </DessousBloc>
                      <DessousBloc titre="Compte">
                        <p className="dessous-texte">
                          La simulation tourne sur le serveur, en base, à chaque action : le hardcore engage
                          vraiment, une victoire se grave.
                        </p>
                      </DessousBloc>
                    </LesDessous>
                  </div>
                </>
              ) : null}

              {erreur ? <p className="erreur">{erreur}</p> : null}

              <div className="menu-boutons">
                <ChoixFlottants
                  variante="plat"
                  type="submit"
                  disabled={enCours}
                  label={enCours ? "…" : "🎬 Lancer la chaîne"}
                />
                <ChoixFlottants
                  variante="plat"
                  style="discret"
                  disabled={enCours}
                  label="← Retour"
                  onClick={() => irA(2)}
                />
              </div>

              {/* ⟦C62 · lot tableau · A-179⟧ « Options » : le lien discret qui rouvre Mode, « Où vit ta partie » et
                  Graine, tels qu'ils étaient. Une fois ouvert, il disparaît et les choix restent : un Hardcore
                  choisi puis recaché serait un piège. Même habillage que « Historique des parties » (étape ①). */}
              {limpide && !options ? (
                <button type="button" className="lien lien-options" disabled={enCours} onClick={() => setOptions(true)}>
                  Options
                </button>
              ) : null}
            </form>
            <BoiteDialogue
              hote={HOTE}
              joueur={joueur(genre)}
              texte={limpide ? REPLIQUE_PRET_A_FILMER : REPLIQUE_ETAPE[3]}
            />
          </>
        ) : null}
      </div>
    </main>
  );
}
